import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Plus, Copy, Pencil, Trash2, ChevronLeft, LogOut, Ticket, MousePointerClick, CalendarCheck, DollarSign, BookOpen,
} from "lucide-react";
import { toast } from "sonner";
import { api, money, authToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const EMPTY = { code: "", influencer_name: "", email: "", discount_type: "none", discount_value: 0, active: true };

const discountLabel = (r) => {
  if (r.discount_type === "percent") return `${r.discount_value}% off`;
  if (r.discount_type === "amount") return `${money(r.discount_value)} off`;
  return "Tracking only";
};

export default function AdminReferrals() {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [guide, setGuide] = useState({ guide_content: "", guide_url: "" });
  const [savingGuide, setSavingGuide] = useState(false);
  const [props, setProps] = useState([]);
  const [guidePid, setGuidePid] = useState("");
  const [withGuide, setWithGuide] = useState([]);

  const load = () => api.get("/admin/referrals").then((r) => setRows(r.data.referrals)).catch(() => setRows([]));
  const loadGuideList = () => api.get("/admin/guides").then((r) => setWithGuide(r.data.property_ids || []));
  useEffect(() => {
    load();
    loadGuideList();
    api.get("/properties").then((r) => {
      const list = r.data.source === "ownerrez" ? (r.data.properties || []) : [];
      setProps(list);
      if (list.length) setGuidePid((list.find((p) => p.name.includes("Grandview")) || list[0]).id);
    });
  }, []);

  useEffect(() => {
    if (!guidePid) { return undefined; }
    let live = true;
    setGuide({ guide_content: "", guide_url: "" });
    api.get(`/admin/guides/${guidePid}`).then((r) => {
      if (live) { setGuide({ guide_content: r.data.guide_content || "", guide_url: r.data.guide_url || "" }); }
    });
    return () => { live = false; };
  }, [guidePid]);

  const saveGuide = async () => {
    setSavingGuide(true);
    try { await api.put(`/admin/guides/${guidePid}`, guide); toast.success("Welcome guide saved"); loadGuideList(); }
    catch { toast.error("Could not save guide"); }
    finally { setSavingGuide(false); }
  };

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (r) => {
    setForm({ code: r.code, influencer_name: r.influencer_name, email: r.email || "", discount_type: r.discount_type, discount_value: r.discount_value, active: r.active });
    setEditId(r.id); setOpen(true);
  };

  const save = async () => {
    if (!form.code.trim() || !form.influencer_name.trim()) return toast.error("Code and influencer name are required");
    setSaving(true);
    try {
      if (editId) await api.put(`/admin/referrals/${editId}`, form);
      else await api.post("/admin/referrals", form);
      toast.success(editId ? "Code updated" : "Code created");
      setOpen(false); await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete code "${r.code}"? This cannot be undone.`)) return;
    try { await api.delete(`/admin/referrals/${r.id}`); toast.success("Deleted"); await load(); }
    catch { toast.error("Delete failed"); }
  };

  const copyLink = (code) => {
    const link = `${window.location.origin}/?ref=${code}`;
    navigator.clipboard.writeText(link);
    toast.success("Share link copied");
  };

  const logout = () => { authToken.clear(); nav("/admin/login"); };

  const totals = (rows || []).reduce((a, r) => ({
    clicks: a.clicks + (r.clicks || 0), bookings: a.bookings + (r.bookings || 0), revenue: a.revenue + (r.revenue || 0),
  }), { clicks: 0, bookings: 0, revenue: 0 });

  return (
    <div className="min-h-screen bg-sand">
      <header className="flex items-center justify-between border-b border-black/10 bg-white px-6 py-4">
        <div className="flex items-center gap-3">
          <Link to="/admin" data-testid="back-to-dashboard" className="flex items-center gap-1 text-sm text-taupe hover:text-ink">
            <ChevronLeft size={16} /> Dashboard
          </Link>
          <span className="text-black/10">|</span>
          <h1 className="flex items-center gap-2 text-xl font-semibold text-ink"><Ticket size={20} className="text-camel" /> Referral Codes</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="new-referral-btn" onClick={openNew} className="rounded-full btn-ink"><Plus size={16} className="mr-1" /> New code</Button>
          <Button data-testid="admin-logout-btn" onClick={logout} variant="outline" className="rounded-full border-black/10"><LogOut size={16} /></Button>
        </div>
      </header>

      <div className="p-6">
        {/* Guest welcome guide */}
        <div className="mb-6 rounded-2xl border border-black/5 bg-white p-5" data-testid="guide-editor">
          <div className="mb-2 flex items-center gap-2">
            <BookOpen size={18} className="text-camel" />
            <h2 className="text-lg font-semibold text-ink">Guest Welcome Guide</h2>
          </div>
          <p className="mb-3 text-sm text-taupe">
            Each property has its own guide. Pick a property, paste its guide — headings (#), lists (-) and [links](url) all render. Guests staying at that property see it after signing in at <span className="font-mono">/guest</span>.
          </p>
          <label className="mb-1 block text-xs font-medium text-taupe">Property</label>
          <Select value={guidePid} onValueChange={setGuidePid}>
            <SelectTrigger data-testid="guide-property-select" className="mb-3 rounded-xl border-black/10 sm:w-80"><SelectValue placeholder="Choose a property" /></SelectTrigger>
            <SelectContent>
              {props.map((p) => (
                <SelectItem key={p.id} value={p.id} data-testid={`guide-property-${p.id}`}>
                  {p.name}{withGuide.includes(p.id) ? " ✓" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <textarea data-testid="guide-content" rows={8} value={guide.guide_content}
            onChange={(e) => setGuide({ ...guide, guide_content: e.target.value })}
            placeholder={"# Welcome to Grandview Estate\n\nCheck-in is at 4pm...\n\n## WiFi\n- Network: ...\n- Password: ..."}
            className="w-full rounded-xl border border-black/10 p-3 text-sm font-mono focus:border-camel focus:outline-none" />
          <label className="mb-1 mt-3 block text-xs font-medium text-taupe">Optional: external guide link (adds an "Open full guide" button)</label>
          <Input data-testid="guide-url" value={guide.guide_url}
            onChange={(e) => setGuide({ ...guide, guide_url: e.target.value })}
            placeholder="https://..." className="rounded-xl border-black/10" />
          <div className="mt-3 flex justify-end">
            <Button data-testid="save-guide-btn" onClick={saveGuide} disabled={savingGuide || !guidePid} className="rounded-full btn-ink">
              {savingGuide ? "Saving…" : "Save guide"}
            </Button>
          </div>
        </div>

        {/* Stat summary */}
        <div className="mb-6 grid gap-4 sm:grid-cols-3">
          {[
            { icon: MousePointerClick, label: "Total link clicks", value: totals.clicks },
            { icon: CalendarCheck, label: "Attributed bookings", value: totals.bookings },
            { icon: DollarSign, label: "Attributed revenue", value: money(totals.revenue) },
          ].map((c) => (
            <div key={c.label} className="rounded-2xl border border-black/5 bg-white p-5">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-cream"><c.icon size={16} strokeWidth={1.5} /></span>
              <div className="mt-3 text-3xl font-medium text-ink">{c.value}</div>
              <div className="overline mt-1">{c.label}</div>
            </div>
          ))}
        </div>

        <div className="overflow-hidden rounded-2xl border border-black/5 bg-white">
          <table className="w-full text-sm" data-testid="referrals-table">
            <thead className="border-b border-black/5 bg-cream/50 text-left text-xs uppercase tracking-wide text-taupe">
              <tr>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Influencer</th>
                <th className="px-4 py-3">Reward</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Clicks</th>
                <th className="px-4 py-3 text-right">Bookings</th>
                <th className="px-4 py-3 text-right">Revenue</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows === null && <tr><td colSpan={8} className="px-4 py-10 text-center text-taupe">Loading…</td></tr>}
              {rows && rows.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-taupe">No codes yet. Create your first one.</td></tr>}
              {rows && rows.map((r) => (
                <tr key={r.id} data-testid={`referral-row-${r.code}`} className="border-b border-black/5 last:border-0">
                  <td className="px-4 py-3 font-mono font-semibold text-ink">{r.code}</td>
                  <td className="px-4 py-3 text-ink/80">
                    <div>{r.influencer_name}</div>
                    {r.email && <div className="text-xs text-taupe">{r.email}</div>}
                  </td>
                  <td className="px-4 py-3 text-ink/80">{discountLabel(r)}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${r.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                      {r.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">{r.clicks}</td>
                  <td className="px-4 py-3 text-right">{r.bookings}</td>
                  <td className="px-4 py-3 text-right font-medium">{money(r.revenue)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <button data-testid={`copy-link-${r.code}`} onClick={() => copyLink(r.code)} title="Copy share link" className="rounded-lg p-1.5 text-taupe hover:bg-cream hover:text-ink"><Copy size={15} /></button>
                      <button data-testid={`edit-${r.code}`} onClick={() => openEdit(r)} title="Edit" className="rounded-lg p-1.5 text-taupe hover:bg-cream hover:text-ink"><Pencil size={15} /></button>
                      <button data-testid={`delete-${r.code}`} onClick={() => remove(r)} title="Delete" className="rounded-lg p-1.5 text-taupe hover:bg-red-50 hover:text-red-600"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="referral-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit referral code" : "New referral code"}</DialogTitle>
            <DialogDescription>Codes are shared as a link ({window.location.origin}/?ref=CODE) or typed at checkout.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-taupe">Code</label>
              <Input data-testid="form-code" placeholder="e.g. JOHNRAMOS15" value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="rounded-xl border-black/10 font-mono uppercase" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-taupe">Influencer name</label>
              <Input data-testid="form-influencer" placeholder="e.g. John Ramos" value={form.influencer_name}
                onChange={(e) => setForm({ ...form, influencer_name: e.target.value })} className="rounded-xl border-black/10" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-taupe">Influencer email <span className="text-taupe/60">(optional — sends them their share link)</span></label>
              <Input data-testid="form-email" type="email" placeholder="e.g. john@example.com" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl border-black/10" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-taupe">Reward type</label>
                <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                  <SelectTrigger data-testid="form-discount-type" className="rounded-xl border-black/10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Tracking only</SelectItem>
                    <SelectItem value="percent">% discount</SelectItem>
                    <SelectItem value="amount">$ discount</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.discount_type !== "none" && (
                <div>
                  <label className="mb-1 block text-xs font-medium text-taupe">{form.discount_type === "percent" ? "Percent off" : "Amount off ($)"}</label>
                  <Input data-testid="form-discount-value" type="number" min="0" value={form.discount_value}
                    onChange={(e) => setForm({ ...form, discount_value: Number(e.target.value) })} className="rounded-xl border-black/10" />
                </div>
              )}
            </div>
            <label className="flex items-center gap-2 text-sm text-ink">
              <Checkbox data-testid="form-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: !!v })} /> Active
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full border-black/10">Cancel</Button>
            <Button data-testid="save-referral-btn" onClick={save} disabled={saving} className="rounded-full btn-ink">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
